import { useCallback } from "react";
import { convertirAMonedaLocal } from "../../../utils/calculateVentas";

export const useVPCierresCalcs = ({
  oportunidades,
  salesBTL,
  negociosCarteleria,
  contratosData,
  oportunidadesOtroPais,
  salesBTLOtroPais,
  negociosCarteleriaOtroPais,
  contratosDataOtroPais,
  pais,
}) => {
  // Cierres Publicidad genérico
  const calcCierresPub = useCallback((data, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((o) => o.estatus === "100%" && !o.eliminada && o.mes_cierre)
      .forEach((o) => {
        const [mesStr, anoStr] = o.mes_cierre.split("-");
        if (Number(anoStr) === ano) valores[Number(mesStr) - 1] += parseFloat(o.importe) || 0;
      });
    return valores;
  }, []);

  // Cierres BTL genérico
  const calcCierresBTL = useCallback((data, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((b) => b.status === 100 && !b.eliminada && b.mes_cierre)
      .forEach((b) => {
        const [mesStr, anoStr] = b.mes_cierre.split("-");
        if (Number(anoStr) === ano) valores[Number(mesStr) - 1] += Number(b.total_investment) || 0;
      });
    return valores;
  }, []);

  // Cierres Hardware genérico
  const calcCierresHW = useCallback((data, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((n) => n.incluye_hardware && !n.eliminada && n.estado === "100%" && n.fecha_cierre)
      .forEach((n) => {
        const fecha = new Date(n.fecha_cierre);
        if (fecha.getFullYear() === ano) valores[fecha.getMonth()] += Number(n.monto_hardware) || 0;
      });
    return valores;
  }, []);

  // Cierres Cartelería genérico (necesita país para conversión)
  const calcCierresCart = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((n) => !n.eliminada && n.estado === "100%" && n.fecha_cierre)
      .forEach((n) => {
        const fecha = new Date(n.fecha_cierre);
        if (fecha.getFullYear() === ano) {
          const pantallas = parseInt(n.cantidad_pantallas) || 1;
          const plazo = parseInt(n.plazo_contrato_meses) || 0;
          const gestion = parseFloat(n.fee_gestion_unitario) || 0;
          const contenidos = n.incluye_contenidos ? parseFloat(n.fee_contenidos_unitario) || 0 : 0;

          // Obtener monedas (UF por defecto para Chile, PEN para Perú)
          const defaultMoneda = paisParam === "Chile" ? "UF" : "PEN";
          const monedaGestion = (n.moneda_gestion || defaultMoneda).toUpperCase();
          const monedaContenidos = (n.moneda_contenidos || defaultMoneda).toUpperCase();

          // Fecha para conversión (último día del mes de cierre)
          const ultimoDiaMes = new Date(ano, fecha.getMonth() + 1, 0);
          const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

          // Convertir a moneda local
          const gestionLocal = convertirAMonedaLocal(gestion * pantallas, monedaGestion, paisParam, fechaConversion);
          const contenidosLocal = convertirAMonedaLocal(
            contenidos * pantallas,
            monedaContenidos,
            paisParam,
            fechaConversion
          );
          const montoTotal = (gestionLocal + contenidosLocal) * plazo;

          valores[fecha.getMonth()] += montoTotal;
        }
      });
    return valores;
  }, []);

  // Cierres Contratos genérico (suma importe_total por mes_cierre)
  const calcCierresContratos = useCallback((data, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => c.estatus === "100%" && !c.eliminada && c.mes_cierre)
      .forEach((c) => {
        const [mesStr, anoStr] = c.mes_cierre.split("-");
        if (Number(anoStr) === ano) valores[Number(mesStr) - 1] += Number(c.importe_total) || 0;
      });
    return valores;
  }, []);

  // Funciones expuestas (país actual)
  const calcularCierresPub = useCallback((ano) => calcCierresPub(oportunidades, ano), [oportunidades, calcCierresPub]);
  const calcularCierresBTL = useCallback((ano) => calcCierresBTL(salesBTL, ano), [salesBTL, calcCierresBTL]);
  const calcularCierresHW = useCallback(
    (ano) => calcCierresHW(negociosCarteleria, ano),
    [negociosCarteleria, calcCierresHW]
  );
  const calcularCierresCart = useCallback(
    (ano) => calcCierresCart(negociosCarteleria, pais, ano),
    [negociosCarteleria, pais, calcCierresCart]
  );

  const calcularCierresContratos = useCallback(
    (ano) => calcCierresContratos(contratosData, ano),
    [contratosData, calcCierresContratos]
  );

  // Funciones expuestas (otro país)
  const otroPais = pais === "Chile" ? "Perú" : "Chile";
  const calcularCierresPubOtro = useCallback(
    (ano) => calcCierresPub(oportunidadesOtroPais, ano),
    [oportunidadesOtroPais, calcCierresPub]
  );
  const calcularCierresBTLOtro = useCallback(
    (ano) => calcCierresBTL(salesBTLOtroPais, ano),
    [salesBTLOtroPais, calcCierresBTL]
  );
  const calcularCierresHWOtro = useCallback(
    (ano) => calcCierresHW(negociosCarteleriaOtroPais, ano),
    [negociosCarteleriaOtroPais, calcCierresHW]
  );
  const calcularCierresCartOtro = useCallback(
    (ano) => calcCierresCart(negociosCarteleriaOtroPais, otroPais, ano),
    [negociosCarteleriaOtroPais, otroPais, calcCierresCart]
  );

  const calcularCierresContratosOtro = useCallback(
    (ano) => calcCierresContratos(contratosDataOtroPais, ano),
    [contratosDataOtroPais, calcCierresContratos]
  );

  return {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  };
};
